/**
 * Echo Job Progress Session Bridge
 *
 * Bridges Laravel Echo `.job.progress.updated` events into the shared
 * job-progress store. One subscription on the user's private jobs channel
 * serves every job (demuxed by `job_id` inside the store). Mounted for
 * authenticated users for the duration of their session.
 */

import { useEcho } from '@laravel/echo-react';
import { useCallback } from 'react';

import {
  JOB_PROGRESS_CONSTANTS,
  JobProgressUtils,
} from '@personal-trainer-app/shared';

import type { EchoJobProgressSessionBridgeProps } from './EchoJobProgressSessionBridge.types';
import type React from 'react';

import { useJobProgressStore } from '@/stores';

export const EchoJobProgressSessionBridge: React.FC<
  EchoJobProgressSessionBridgeProps
> = ({ username, debug = false }) => {
  const upsert = useJobProgressStore((state) => state.upsert);
  const channelName = JobProgressUtils.createUserJobsChannel(username);

  const handleEvent = useCallback(
    (payload: unknown) => {
      const progress = JobProgressUtils.extractJobProgressFromPayload(payload);

      if (!progress) {
        if (debug) {
          console.warn('[EchoJobProgressSessionBridge] Unusable payload:', {
            channelName,
            payload,
          });
        }
        return;
      }

      if (debug) {
        console.log('[EchoJobProgressSessionBridge] Progress:', {
          channelName,
          jobId: progress.job_id,
          jobType: progress.job_type,
          status: progress.status,
          percentage: progress.percentage,
        });
      }

      upsert(progress);
    },
    [upsert, channelName, debug]
  );

  useEcho<unknown>(
    channelName,
    JOB_PROGRESS_CONSTANTS.JOB_PROGRESS_EVENT,
    handleEvent,
    [handleEvent],
    'private'
  );

  return null;
};

export default EchoJobProgressSessionBridge;
